import { ContainedBtn, MenuBtn } from '@components/Buttons';
import { MenuItemData } from '@components/Navigation';
import { featureFlags } from '@core/consts';
import { merchantCategoryRoutes, navigateAppStore, Route, Routes } from '@navigation';
import { mc, StyleProps } from '@styles';
import { TestIdProps } from '@utils';
import { compact, sortBy } from 'lodash';
import React, { FC, useEffect, useState } from 'react';

import Caregiving101Icon from './assets/caregiving101.inline.svg';
import CareSupportIcon from './assets/careSupport.inline.svg';
import EndOfCareIcon from './assets/endOfCare.inline.svg';
import FinanceIcon from './assets/finance.inline.svg';
import HealthIcon from './assets/health.inline.svg';
import HousingIcon from './assets/housing.inline.svg';
import LegalIcon from './assets/legal.inline.svg';
import QuickTipsIcon from './assets/quickTips.inline.svg';
import ViewAllIcon from './assets/viewAll.inline.svg';
import NavigationHeaderBtn from './components/Btn';
import NavigationHeaderLogo from './components/Logo';
import NavigationHeaderMobileMenu from './components/MobileDropdown';
import NavigationTailsViewDropdown from './components/TailsViewDropdown';
import NavigationTreeViewDropdown from './components/TreeViewDropdown';

type Props = StyleProps & TestIdProps;

const subroutesToMenuItems = (route: Route): MenuItemData[] => {
  const items: MenuItemData[] = [{ title: route.label, url: route.url }];
  if (route.sub) {
    const sub = sortBy(Object.values(route.sub), 'index');
    sub.forEach(subroute => {
      items.push(...subroutesToMenuItems(subroute));
    });
  }
  return items;
};

const menuItems: MenuItemData[] = [
  {
    title: 'Get extra help',
    subtitle: 'Trusted support',
    url: '/care-support',
    view: 'tree',
    items: compact([
      {
        title: 'Care support',
        subtitle: 'How care happens',
        url: merchantCategoryRoutes.careSupport.url,
        items: subroutesToMenuItems(merchantCategoryRoutes.careSupport),
      },
      featureFlags.financeMegaCategoryEnabled && {
        title: 'Finance',
        subtitle: 'Affording care',
        url: merchantCategoryRoutes.finance.url,
        items: subroutesToMenuItems(merchantCategoryRoutes.finance),
      },
      {
        title: 'Health',
        subtitle: 'Care wellbeing',
        url: merchantCategoryRoutes.health.url,
        items: subroutesToMenuItems(merchantCategoryRoutes.health),
      },
      {
        title: 'Housing',
        subtitle: 'Where care happens',
        url: merchantCategoryRoutes.housing.url,
        items: subroutesToMenuItems(merchantCategoryRoutes.housing),
      },
      {
        title: 'Legal',
        subtitle: 'Protecting care',
        url: merchantCategoryRoutes.legal.url,
        items: subroutesToMenuItems(merchantCategoryRoutes.legal),
      },
    ]),
  },
  {
    url: Routes.guidesAndTips.url,
    title: 'Guides & tips',
    subtitle: 'Articles, blogs, & stories',
    description: 'Learn strategies to manage what is happening in your caregiving world',
    view: 'tiles',
    items: [
      {
        title: 'Caregiving 101',
        url: `/guides-and-tips/caregiving-101`,
        icon: <Caregiving101Icon />,
      },
      {
        title: 'Health',
        url: `/guides-and-tips/health`,
        icon: <HealthIcon />,
      },
      {
        title: 'Quick Tips',
        url: `/guides-and-tips/quick-tips`,
        icon: <QuickTipsIcon />,
      },
      {
        title: 'Care Support',
        url: `/guides-and-tips/care-support`,
        icon: <CareSupportIcon />,
      },
      {
        title: 'Housing',
        url: `/guides-and-tips/housing`,
        icon: <HousingIcon />,
      },
      {
        title: 'End of Care',
        url: `/guides-and-tips/end-of-care`,
        icon: <EndOfCareIcon />,
      },
      {
        title: 'Finance',
        url: `/guides-and-tips/finance`,
        icon: <FinanceIcon />,
      },
      {
        title: 'Legal',
        url: `/guides-and-tips/legal`,
        icon: <LegalIcon />,
      },
      { title: 'View All Guides & Tips', url: '/guides-and-tips', icon: <ViewAllIcon /> },
    ],
  },
  { title: Routes.app.label, subtitle: 'How it works', url: Routes.app.url },
  { title: Routes.about.label, subtitle: 'The CircleOf story', url: Routes.about.url },
];

export const NavigationHeader: FC<Props> = ({ testId, className }) => {
  const [hoveredMainMenuItem, setHoveredMainMenuItem] = useState<MenuItemData | undefined>();
  const [dropdownHovered, setDropdownHovered] = useState<Record<number, boolean>>({});
  const [mobileMenuVisible, setMobileMenuVisible] = useState<boolean>(false);

  useEffect(() => {
    const body = document.getElementsByTagName('body');
    if (body.length) {
      body[0].style.overflowY = mobileMenuVisible ? 'hidden' : '';
      body[0].style.height = mobileMenuVisible ? '100%' : '';
    }
    const html = document.getElementsByTagName('html');
    if (html.length) {
      html[0].style.overflowY = mobileMenuVisible ? 'hidden' : '';
      html[0].style.height = mobileMenuVisible ? '100%' : '';
    }
  }, [mobileMenuVisible]);

  const handleGetAppClick = () => {
    navigateAppStore(() => {
      const element = document.getElementById('app-download-footer');
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.location.href = Routes.app.url;
    });
  };

  return (
    <>
      <nav
        data-testid={testId}
        className={mc('h-20 w-full lg:h-24', 'px-8 lg:px-10', 'relative', 'flex flex-row justify-center', 'bg-white', className)}
      >
        <NavigationHeaderLogo
          className="h-full absolute top-0 left-8 lg:left-20 pl-2"
          onClick={() => setMobileMenuVisible(false)}
        />
        <div className={mc('h-full', 'flex flex-row flex-1', 'max-w-[1072px]')}>
          {/* Main desktop menu */}
          <div className={mc('h-full', 'flex flex-1 justify-end items-center')}>
            {menuItems.map((item, index) => (
              <NavigationHeaderBtn
                key={index}
                className={mc('hidden lg:flex', 'h-full min-w-[110px] xl:min-w-[130px]')}
                item={item}
                active={hoveredMainMenuItem?.title === item.title || dropdownHovered[index]}
                onClick={() => setHoveredMainMenuItem(undefined)}
                onMouseEnter={() => setHoveredMainMenuItem(item)}
                onMouseLeave={() => setHoveredMainMenuItem(undefined)}
              />
            ))}
          </div>
          {/* Menu Btn */}
          <div className={mc('lg:hidden h-full', 'flex items-center justify-center')}>
            <MenuBtn
              testId={testId ? `${testId}-menu-btn` : undefined}
              icon={!mobileMenuVisible ? 'menu' : 'close'}
              onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
            />
          </div>
          {/* Menu Btn end */}
          {/* Get the app button */}
          <div className="h-full hidden lg:flex items-center justify-center ml-5">
            <ContainedBtn onClick={handleGetAppClick} color="orange" style={{ paddingTop: 10, paddingBottom: 10 }}>
              {'Get the App'}
            </ContainedBtn>
          </div>
          {/* Get the app button end */}
        </div>
      </nav>
      <NavigationTreeViewDropdown
        className={mc(
          'absolute z-10 top-20 lg:top-24 left-0 right-0',
          !featureFlags.financeMegaCategoryEnabled ? 'max-h-96' : 'max-h-[420px]',
          'overflow-hidden border-b',
        )}
        style={hoveredMainMenuItem?.title === menuItems[0].title || dropdownHovered[0] ? undefined : { display: 'none' }}
        item={menuItems[0]}
        onMouseEnter={() => setDropdownHovered({ ...dropdownHovered, 0: true })}
        onMouseLeave={() => setDropdownHovered({ ...dropdownHovered, 0: false })}
        onClose={() => setDropdownHovered({ ...dropdownHovered, 0: false })}
      />
      <NavigationTailsViewDropdown
        className={mc(
          'absolute z-10 top-20 lg:top-24 left-0 right-0',
          !featureFlags.financeMegaCategoryEnabled ? 'max-h-96' : 'max-h-[420px]',
          'overflow-hidden border-b',
        )}
        style={hoveredMainMenuItem?.title === menuItems[1].title || dropdownHovered[1] ? undefined : { display: 'none' }}
        item={menuItems[1]}
        onMouseEnter={() => setDropdownHovered({ ...dropdownHovered, 1: true })}
        onMouseLeave={() => setDropdownHovered({ ...dropdownHovered, 1: false })}
        onClose={() => setDropdownHovered({ ...dropdownHovered, 1: false })}
      />
      {mobileMenuVisible && (
        <NavigationHeaderMobileMenu
          testId={testId ? `${testId}-mobile-menu` : undefined}
          className={mc('lg:hidden', 'fixed z-10 top-20 left-0 right-0 bottom-0')}
          items={menuItems}
          onClose={() => setMobileMenuVisible(false)}
        />
      )}
    </>
  );
};

export type NavigationHeaderProps = Props;
export default NavigationHeader;
